import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Default homepage copy. Admin edits are stored in the DB and override these.
export const DEFAULT_CONTENT = {
  hero_eyebrow: 'Established 1998 • Space Memorabilia Auctions Since 2009',
  hero_headline: 'Trusted Space Memorabilia Auctions Since 2009',
  hero_headline_color: '#FFFFFF',
  hero_headline_size: 'medium',
  hero_subtitle: 'Authentic flown artifacts, astronaut autographs, hardware, and documents — researched, documented, and brought to collectors worldwide.',

  upcoming_auction_date: '',

  trust_1_title: 'Provenance & Research',
  trust_1_text: 'Every item is supported by provenance, research, and documented history.',
  trust_2_title: 'Careful Custody',
  trust_2_text: 'Secure, careful handling and storage appropriate for delicate historical material.',
  trust_3_title: '25+ Years of Experience',
  trust_3_text: 'Founded in 1998, with dedicated space memorabilia auctions held since 2009.',

  about_eyebrow: 'About Lunar Legacies',
  about_heading: '25+ Years in Space Memorabilia',
  about_paragraph: 'Founded in 1998 by Don Willis, Lunar Legacies has specialized in authentic Apollo, Gemini, Mercury, Skylab, and Space Shuttle memorabilia, astronaut autographs, flown artifacts, hardware, and documents. Dedicated auctions began in 2009, and every item is supported by provenance, research, and documented history.',
  about_quote: 'With over 25 years in the field, our focus is straightforward: accurate research, sound provenance, and careful stewardship of the artifacts entrusted to us.',

  contact_heading: 'Contact Lunar Legacies',
  contact_intro: "Questions about an item, a collection, or a possible consignment? We're glad to help — inquiries are handled confidentially.",

  collector_heading: 'Join the Lunar Legacies Collector List',
  collector_intro: 'Be the first to receive upcoming auction announcements, featured artifacts, and rare space memorabilia opportunities.',

  // Admin-created custom homepage sections: [{ id, title, body }]
  custom_blocks: [],
};

// Human-friendly labels + grouping for the admin editor.
export const CONTENT_FIELDS = [
  { group: 'Hero', key: 'hero_eyebrow', label: 'Hero eyebrow (small line above)', type: 'text' },
  { group: 'Hero', key: 'hero_headline', label: 'Hero Text Box 1 — large heading (Enter for new line)', type: 'textarea' },
  { group: 'Hero', key: 'hero_headline_color', label: 'Hero Box 1 font color (hex, e.g. #C9A24B)', type: 'colortext' },
  { group: 'Hero', key: 'hero_headline_size', label: 'Hero Box 1 font size', type: 'select', options: [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium (default)' },
    { value: 'large', label: 'Large' },
    { value: 'xlarge', label: 'Extra Large' },
  ] },
  { group: 'Hero', key: 'hero_subtitle', label: 'Hero Text Box 2 — subtext (Enter for new line)', type: 'textarea' },
  { group: 'Auction Preview', key: 'upcoming_auction_date', label: 'Upcoming auction date (shown on Auction Preview page)', type: 'text' },
  { group: 'Trust Section', key: 'trust_1_title', label: 'Card 1 title', type: 'text' },
  { group: 'Trust Section', key: 'trust_1_text', label: 'Card 1 text', type: 'textarea' },
  { group: 'Trust Section', key: 'trust_2_title', label: 'Card 2 title', type: 'text' },
  { group: 'Trust Section', key: 'trust_2_text', label: 'Card 2 text', type: 'textarea' },
  { group: 'Trust Section', key: 'trust_3_title', label: 'Card 3 title', type: 'text' },
  { group: 'Trust Section', key: 'trust_3_text', label: 'Card 3 text', type: 'textarea' },
  { group: 'About', key: 'about_eyebrow', label: 'About eyebrow', type: 'text' },
  { group: 'About', key: 'about_heading', label: 'About heading', type: 'text' },
  { group: 'About', key: 'about_paragraph', label: 'About paragraph', type: 'textarea' },
  { group: 'About', key: 'about_quote', label: 'About closing quote', type: 'textarea' },
  { group: 'Contact', key: 'contact_heading', label: 'Contact heading', type: 'text' },
  { group: 'Contact', key: 'contact_intro', label: 'Contact intro', type: 'textarea' },
  { group: 'Collector List', key: 'collector_heading', label: 'Collector List heading', type: 'text' },
  { group: 'Collector List', key: 'collector_intro', label: 'Collector List intro', type: 'textarea' },
];

const SiteContentContext = createContext({ content: DEFAULT_CONTENT, loading: true, refresh: () => {} });

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/content`);
      setContent({ ...DEFAULT_CONTENT, ...(res.data || {}) });
    } catch (e) {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SiteContentContext.Provider value={{ content, loading, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => useContext(SiteContentContext);
